import menuService, { type Menu } from '../../services/menu-service'
import { ref } from 'vue'

export interface INavigationRoute {
  name: string
  displayName: string
  meta: { icon: string }
  children?: INavigationRoute[]
}

// Fallback default routes (in case API fails or user is not logged in)
function getDefaultRoutes(): INavigationRoute[] {
  return [
    {
      name: 'time-in-out',
      displayName: 'Time In / Out',
      meta: { icon: 'schedule' },
    },
    {
      name: 'settings',
      displayName: 'Settings',
      meta: { icon: 'settings' },
    },
  ]
}

// Convert Menu from API to INavigationRoute format
function convertMenuToRoute(menu: Menu): INavigationRoute {
  const route: INavigationRoute = {
    name: menu.name,
    displayName: menu.display_name,
    meta: {
      icon: menu.icon || 'circle',
    },
  }

  if (menu.children && menu.children.length > 0) {
    route.children = menu.children.map(convertMenuToRoute)
  }

  return route
}

// Reactive routes that will be loaded from API
// Initialize with fallback routes so UI doesn't break
export const dynamicRoutes = ref<INavigationRoute[]>(getDefaultRoutes())
export const isLoadingRoutes = ref(false)
export const routesError = ref<string | null>(null)

// Load menus from API
export async function loadUserMenus(forceRefresh: boolean = false) {
  // Don't load if no auth token
  if (!localStorage.getItem('auth_token')) {
    console.log('No auth token, using default routes')
    dynamicRoutes.value = getDefaultRoutes()
    return dynamicRoutes.value
  }

  isLoadingRoutes.value = true
  routesError.value = null

  try {
    // Force refresh when OU changes to get OU-specific menus
    const menus = await menuService.getUserMenus(forceRefresh)
    dynamicRoutes.value = menus.map(convertMenuToRoute)
    return dynamicRoutes.value
  } catch (error: any) {
    console.error('Failed to load user menus:', error)
    routesError.value = error.message || 'Failed to load menus'
    // Fallback to default routes if API fails
    dynamicRoutes.value = getDefaultRoutes()
    return dynamicRoutes.value
  } finally {
    isLoadingRoutes.value = false
  }
}

// Export default for backward compatibility
export default {
  root: {
    name: '/',
    displayName: 'navigationRoutes.home',
  },
  get routes() {
    return dynamicRoutes.value
  },
  loadUserMenus,
}
